import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { stopScroll } from "../lib/smooth";

export const Lightbox = ({ looks, index, setIndex, title, id }) => {
  const open = index !== null;
  const n = looks.length;
  const prev = () => setIndex((i) => (i - 1 + n) % n);
  const next = () => setIndex((i) => (i + 1) % n);

  useEffect(() => {
    if (!open) return undefined;
    stopScroll(true);
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % n);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); stopScroll(false); };
  }, [open, n, setIndex]);

  const look = open ? looks[index] : null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="lb"
          data-testid={`lightbox-${id}`}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex flex-col bg-[#161513] text-[#FAF8F5]"
        >
          <div className="flex items-center justify-between px-5 py-5 sm:px-10">
            <p className="text-xs tracking-[0.28em] uppercase text-[#CDBFA8]">{title} · {index + 1} / {n}</p>
            <button onClick={() => setIndex(null)} aria-label="Close" data-testid="lightbox-close" className="rounded-full border border-white/30 p-2 hover:bg-white/10"><X className="h-5 w-5" /></button>
          </div>
          <div className="relative flex flex-1 items-center justify-center px-4 pb-6 sm:px-20">
            <AnimatePresence initial={false}>
              <motion.figure key={look.src} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }} className="absolute inset-0 flex flex-col items-center justify-center px-4 pb-6 sm:px-20">
                <img src={look.src} alt={look.title} className="max-h-[68vh] w-auto max-w-full object-contain" />
                <figcaption className="mt-5 text-center">
                  <p className="font-display text-3xl">{look.title}</p>
                  <p className="mt-1 text-base text-[#D6CFC4]">{look.note}</p>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
            <button onClick={prev} aria-label="Previous" data-testid="lightbox-prev" className="absolute left-2 z-10 rounded-full border border-white/30 bg-black/30 p-3 hover:bg-white/10 sm:left-6"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={next} aria-label="Next" data-testid="lightbox-next" className="absolute right-2 z-10 rounded-full border border-white/30 bg-black/30 p-3 hover:bg-white/10 sm:right-6"><ChevronRight className="h-5 w-5" /></button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
